import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'

import { Toggle } from '../../common/components/Toggle'
import { SelectRfidTagDialog } from '../../common/dialogs/select-rfid-tag-dialog'
import {
    ChargePoint,
    useChargePointOverrideScheduleForOngoingChargingSessionMutation,
    useChargePointRemoteStartMutation,
    useChargePointRemoteStopMutation,
    useOcppCommandUpdateConfigKeyMutation,
    useGreenChargingChargePointStatusQuery,
    useSmartChargingScheduleGetByChargePointQuery,
    useChargePointUpdateMutation,
    OnOffMode,
} from '../../common/redux/api/web-api'
import Text from '../../ui/Text'
import { isAdminDomain, isUserappDomain } from '../currentDomain'
import findChargingConnector from '../findChargingConnector'
import { getConfigValue } from '../get-config-value'
import { remoteStartIsAvailable, remoteStopIsAvailable } from '../get-connector-state'
import { KEY_CABLE_LOCK, KEY_FREE_CHARGING } from '../ocpp-aura-connector-control'
import { RemoteControlRow } from '../remote-control-row'
import { RemoteControlWidget } from '../remote-control-widget'
import { RemoteOcppDimmerControl } from '../remote-ocpp-dimmer-control'
import setOcppConfigValue from '../setOcppConfigValue'
import { onGoingTransactionStates } from '../charge-point-dawn-control-widget'
import { showErrorMessagesForIgnoreSchedule } from '../../common/utils/chargePointUtils'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'
import { SelectButton } from '../../common/uikit/SelectButton'

enum NfcLockState {
    ON = 'false',
    OFF = 'true',
}

enum CableLockState {
    ON = 'true',
    OFF = 'false',
}

export type ChargePointLunaControlWidgetProps = {
    isReadOnly?: boolean
    chargePoint: ChargePoint
}

export function ChargePointLunaControlWidget({
    isReadOnly = false,
    chargePoint,
}: ChargePointLunaControlWidgetProps): React.ReactElement {
    const text = useText()
    const [configKeys, setConfigKeys] = useState(chargePoint?.ocppConfigKeys || [])
    const [chargingConnector, setChargingConnector] = useState(
        findChargingConnector(chargePoint?.connectors ?? []) ?? null
    )
    const [selectRfidDialogIsOpen, setSelectRfidDialogIsOpen] = useState(false)
    const { data: chargePointStatus } = useGreenChargingChargePointStatusQuery({ chargePointId: chargePoint.id })
    const [saveConfigKey] = useOcppCommandUpdateConfigKeyMutation()
    const [remoteStart] = useChargePointRemoteStartMutation()
    const [remoteStop] = useChargePointRemoteStopMutation()

    const isOnline = chargePoint.chargePointStatus !== 'Offline'
    const isNfcLockOn = getConfigValue(KEY_FREE_CHARGING, configKeys) === NfcLockState.ON
    const isCableLockOn = getConfigValue(KEY_CABLE_LOCK, configKeys) === CableLockState.ON

    const remoteStartIsVisible = useMemo(() => remoteStartIsAvailable(chargingConnector), [chargingConnector])
    const remoteStopIsVisible = useMemo(() => remoteStopIsAvailable(chargingConnector), [chargingConnector])
    const [overrideSchedule] = useChargePointOverrideScheduleForOngoingChargingSessionMutation()

    const toast = useToast()

    const { data: schedules } = useSmartChargingScheduleGetByChargePointQuery({ chargePointId: chargePoint.id })

    const connectorHasSchedule = (connectorId: number) => {
        return schedules?.some((schedule) => schedule.connectorId === connectorId)
    }

    const [updateChargePoint] = useChargePointUpdateMutation()

    useEffect(() => {
        if (chargePoint) {
            setConfigKeys(chargePoint.ocppConfigKeys ?? [])
            setChargingConnector(findChargingConnector(chargePoint.connectors ?? []) ?? null)
        }
    }, [chargePoint])

    const toggleOcppConfigValue = async (key: string, value: string) => {
        const updatedConfigKeys = setOcppConfigValue(key, value, configKeys)

        if (!(updatedConfigKeys && chargePoint)) return
        await saveConfigKey({
            chargePointId: chargePoint.id,
            keyValue: updatedConfigKeys.find((configKeys) => configKeys.key === key),
        })
            .unwrap()
            .then(() => setConfigKeys(updatedConfigKeys))
            .catch(() => toast.notifyError(text('msgRemoteControlCommandFailed')))
    }

    const handleRemoteStart = async (rfidTag?: string) => {
        await remoteStart({
            chargePointId: chargePoint.id,
            connectorId: 1, //Luna has only one connector
            rfidTag,
        })
            .unwrap()
            .then(() => toast.notifySuccess(text('msgRemoteStartSuccess')))
            .catch(() => toast.notifyError(text(isAdminDomain ? 'msgRemoteStartAdminFailed' : 'msgRemoteStartFailed')))
    }

    const handleRemoteControl = async () => {
        if (remoteStopIsVisible) {
            await remoteStop({
                chargePointId: chargePoint.id,
                connectorId: 1, //Luna has only one connector
            })
                .unwrap()
                .then(() => toast.notifySuccess(text('msgRemoteStopSuccess')))
                .catch(() =>
                    toast.notifyError(text(isAdminDomain ? 'msgRemoteStopAdminFailed' : 'msgRemoteStopFailed'))
                )
        } else {
            if (isNfcLockOn) {
                setSelectRfidDialogIsOpen(true)
            } else if (chargingConnector?.defaultNfcTagId) {
                handleRemoteStart(chargingConnector?.defaultNfcTagId)
            } else {
                toast.notifyError(text('msgRemoteStartFailed'))
            }
        }
    }

    const handleOverrideSchedule = async () => {
        await overrideSchedule({
            chargePointId: chargePoint.id,
            connectorId: 1,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgIgnoreScheduleSuccess'))
            })
            .catch((error) => {
                toast.notifyError(showErrorMessagesForIgnoreSchedule(error.data.error))
            })
    }

    const showStartStopChargingButton = !isUserappDomain && isOnline && (remoteStartIsVisible || remoteStopIsVisible)
    const showIgnoreScheduleButton =
        !isUserappDomain && isOnline && remoteStopIsVisible && chargingConnector?.mode === 'Schedule'

    const handleConnectorStateChange = (connectorId: number) => async (state: OnOffMode) => {
        if (state === 'Schedule' && !connectorHasSchedule(connectorId)) {
            return confirmDialog({
                header: text('msgNoSchedulesHeader'),
                message: text('msgNoSchedulesText'),
                acceptClassName: 'hidden',
                rejectLabel: text('btnClose'),
            })
        }

        // Optimistically update the UI immediately
        setChargingConnector((prev) => (prev ? { ...prev, mode: state } : prev))

        try {
            await updateChargePoint({
                chargePoint: {
                    ...chargePoint,
                    connectors: chargePoint.connectors?.map((c) =>
                        c.connectorId === connectorId ? { ...c, mode: state } : c
                    ),
                },
            }).unwrap()
        } catch (error) {
            // Revert the optimistic update on error
            setChargingConnector(findChargingConnector(chargePoint.connectors ?? []) ?? null)
            toast.notifyError(text('msgFailedToUpdateAvailability'))
        }
    }
    return (
        <RemoteControlWidget
            greenChargingStatus={chargePointStatus?.greenChargingStatus}
            isReadOnly={isReadOnly}
            chargePoint={chargePoint}
            title={'lblLunaControlOcppWidget'}
        >
            {chargingConnector && (
                <RemoteControlRow
                    title={
                        <div data-testid={`chargerText-${chargePoint.id}`}>
                            <Text code="lblCharger" />
                        </div>
                    }
                    remoteChargingControl={
                        <div className="flex gap-2 flex-column sm:flex-row">
                            {showStartStopChargingButton && (
                                <Button
                                    tooltip={
                                        remoteStartIsVisible ? text('lblStartChargingInfo') : text('lblStopSession')
                                    }
                                    size="small"
                                    data-testid="remote-control-button"
                                    onClick={handleRemoteControl}
                                    label={text(remoteStopIsVisible ? 'lblStop' : 'lblStart')}
                                    outlined
                                    severity="info"
                                    icon={remoteStopIsVisible ? 'pi pi-stop-circle' : 'pi pi-play-circle'}
                                />
                            )}
                            {showIgnoreScheduleButton && (
                                <Button
                                    tooltip={text('lblIgnoreSchedule')}
                                    style={{ padding: '6px' }}
                                    data-testid="remote-control-button-ignoreSchedule"
                                    onClick={handleOverrideSchedule}
                                    outlined
                                    severity="info"
                                    icon="pi pi-calendar-times"
                                />
                            )}
                        </div>
                    }
                    button={
                        <SelectButton<OnOffMode>
                            id={`charger-mode-${chargePoint.id}`}
                            ariaLabel={text('lblCharger')}
                            options={[
                                { label: text('lblOff'), value: 'Off' },
                                { label: text('lblConnectorSchedule'), value: 'Schedule' },
                                { label: text('lblConnectorAlwaysOn'), value: 'On' },
                            ]}
                            value={chargingConnector.mode}
                            onChange={handleConnectorStateChange(1)}
                            disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                            className="w-20rem max-w-full"
                            data-testid={`toggle-charger-${chargePoint.id}`}
                        />
                    }
                />
            )}

            <RemoteControlRow
                title={
                    <div data-testid={`rfidText-${chargePoint.id}`}>
                        <Text code="lblRFIDLock" />
                    </div>
                }
                button={
                    <Toggle
                        toggleFn={() =>
                            toggleOcppConfigValue(KEY_FREE_CHARGING, isNfcLockOn ? NfcLockState.OFF : NfcLockState.ON)
                        }
                        isToggled={isNfcLockOn}
                        disabled={
                            isReadOnly ||
                            onGoingTransactionStates.includes(chargePoint.connectors[0]?.ocppStatus) ||
                            !isOnline ||
                            chargePoint?.ocppEnabled
                        }
                        data-testid={`toggle-rfid-${chargePoint.id}`}
                    />
                }
            />
            <RemoteOcppDimmerControl chargePoint={chargePoint} isReadOnly={isReadOnly} />

            <RemoteControlRow
                button={
                    <Toggle
                        toggleFn={() =>
                            toggleOcppConfigValue(
                                KEY_CABLE_LOCK,
                                isCableLockOn ? CableLockState.OFF : CableLockState.ON
                            )
                        }
                        isToggled={isCableLockOn}
                        data-testid={`toggle-cableLock-${chargePoint.id}`}
                        disabled={!isOnline || chargePoint?.ocppEnabled}
                    />
                }
                title={
                    <div data-testid={`toggleText-cableLock-${chargePoint.id}`}>
                        <Text code="lblCableLock" />
                    </div>
                }
            />

            <SelectRfidTagDialog
                visible={selectRfidDialogIsOpen}
                chargePointId={chargePoint.id}
                connectorId={chargingConnector?.connectorId ?? 1}
                onStartCharging={(rfidTag) => {
                    handleRemoteStart(rfidTag)
                    setSelectRfidDialogIsOpen(false)
                }}
                onHide={() => setSelectRfidDialogIsOpen(false)}
            />
        </RemoteControlWidget>
    )
}
